
import React, { useRef, useEffect, useCallback } from 'react';
import { cn } from '@/lib/utils';
import { useGlobalSearch } from '@/hooks/useGlobalSearch';
import { useGlobalSearchUI } from './hooks/useGlobalSearchUI';
import { SearchInput } from './components/SearchInput';
import { SearchDropdown } from './components/SearchDropdown';

interface GlobalSearchProps {
  className?: string;
  placeholder?: string;
  isMobile?: boolean;
}

const GlobalSearch: React.FC<GlobalSearchProps> = ({ 
  className, 
  placeholder = "Поиск по всем данным...",
  isMobile = false 
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);

  const {
    query,
    setQuery,
    results,
    loading,
    error,
    searchHistory,
    clearResults
  } = useGlobalSearch();

  const {
    isOpen,
    setIsOpen,
    selectedIndex,
    setSelectedIndex,
    showDropdown,
    handleResultClick,
    handleShowAllResults,
    handleKeyDown
  } = useGlobalSearchUI({
    query,
    results,
    searchHistory,
    clearResults
  });

  // Memoized event handlers to prevent unnecessary re-renders
  const handleClickOutside = useCallback((event: MouseEvent) => {
    if (resultsRef.current && !resultsRef.current.contains(event.target as Node)) {
      setIsOpen(false);
      setSelectedIndex(-1);
    }
  }, [setIsOpen, setSelectedIndex]);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [handleClickOutside]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
    setSelectedIndex(-1);
    setIsOpen(true);
  }, [setQuery, setSelectedIndex, setIsOpen]);

  const handleInputFocus = useCallback(() => {
    setIsOpen(true);
  }, [setIsOpen]);

  const handleClearSearch = useCallback(() => {
    setQuery('');
    clearResults();
    setIsOpen(false);
    setSelectedIndex(-1);
    inputRef.current?.focus();
  }, [setQuery, clearResults, setIsOpen, setSelectedIndex]);

  const handleHistoryClick = useCallback((historyQuery: string) => {
    setQuery(historyQuery);
    setIsOpen(true);
  }, [setQuery, setIsOpen]);

  const handleKeyDownWithHistory = useCallback((e: React.KeyboardEvent) => {
    const result = handleKeyDown(e);
    
    if (typeof result === 'string') {
      if (result === 'escape') {
        inputRef.current?.blur();
      } else {
        // It's a history query
        handleHistoryClick(result);
      }
    }
  }, [handleKeyDown, handleHistoryClick]);

  return (
    <div className={cn("relative w-full max-w-md", className)} ref={resultsRef}>
      <SearchInput
        ref={inputRef}
        query={query}
        placeholder={placeholder}
        isMobile={isMobile}
        onChange={handleInputChange}
        onFocus={handleInputFocus}
        onKeyDown={handleKeyDownWithHistory}
        onClear={handleClearSearch}
      />

      {showDropdown && (
        <SearchDropdown
          loading={loading}
          error={error}
          query={query}
          results={results}
          searchHistory={searchHistory}
          selectedIndex={selectedIndex}
          onResultClick={handleResultClick}
          onHistoryClick={handleHistoryClick}
          onShowAllResults={handleShowAllResults}
        />
      )}
    </div>
  );
};

export default GlobalSearch;
